import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, Mail, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';

const GoldMLogo: React.FC<{ size?: number }> = ({ size = 64 }) => {
  const id = `mg${size}`;
  const pad = size * 0.12;
  const inner = size - pad * 2;
  const pts = [
    [0.23, 0.88], [0.23, 0.40], [0.42, 0.65], [0.50, 0.50],
    [0.58, 0.35], [0.77, 0.62], [0.77, 0.88],
  ].map(([x, y]) => `${(x! * inner + pad).toFixed(2)},${(y! * inner + pad).toFixed(2)}`).join(' ');
  const r = (size * 0.12).toFixed(2);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`${id}-stroke`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F5D78E" />
          <stop offset="50%" stopColor="#C9A84C" />
          <stop offset="100%" stopColor="#8B6914" />
        </linearGradient>
        <linearGradient id={`${id}-bg`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1a1035" />
          <stop offset="100%" stopColor="#0d0820" />
        </linearGradient>
      </defs>
      <rect width={size} height={size} rx={r} fill={`url(#${id}-bg)`} />
      <rect x="0.5" y="0.5" width={size - 1} height={size - 1} rx={r} stroke="#C9A84C" strokeWidth="0.8" opacity="0.4" />
      <polyline points={pts} stroke={`url(#${id}-stroke)`} strokeWidth={size * 0.065} strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // Existing users logging in skip the onboarding tour on all browsers
      localStorage.setItem('onboarding_completed', 'true');
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <SEO
        title="Sign In"
        description="Sign in to your My Investments account to access your AI-powered finance dashboard, trading signals, and portfolio analytics."
        canonical="https://my-investments.io/login"
      />
      {/* Top nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 h-12 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/40">
        <div className="max-w-6xl mx-auto px-4 lg:px-6 h-full flex items-center justify-between">
          <Link to="/welcome" className="flex items-center gap-2">
            <GoldMLogo size={28} />
            <span className="text-sm font-bold text-white tracking-wide">my investments</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/mcp" className="text-sm text-slate-400 hover:text-white transition-colors">MCP</Link>
            <Link to="/register" className="text-sm px-4 py-1.5 min-h-[44px] inline-flex items-center bg-white text-black font-semibold rounded-full hover:bg-gray-100 transition-colors">Get Started</Link>
          </div>
        </div>
      </nav>

      <div className="flex items-center justify-center min-h-screen p-4 pt-16">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <GoldMLogo size={64} />
          </div>
          <h1 className="text-2xl font-bold text-white">Welcome Back</h1>
          <p className="text-slate-400 mt-2">Sign in to your my investments account</p>
        </div>

        {/* Login Form */}
        <div className="bg-slate-800 rounded-xl border border-slate-700/50 p-6 lg:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 text-red-400 bg-red-400/10 px-4 py-3 rounded-lg text-sm">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-slate-300">Password</label>
                <Link to="/forgot-password" className="text-sm text-teal-400 hover:text-teal-300">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-slate-900/30 border-t-slate-900 rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn size={18} />
                  Sign In
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-400 text-sm">
              Don't have an account?{' '}
              <Link to="/register" className="text-teal-400 hover:text-teal-300 font-medium">
                Create one
              </Link>
            </p>
          </div>
        </div>

      </div>
      </div>
    </div>
  );
};

export default Login;
